import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Barcode from "react-barcode";
import {
  createProduct,
  selectCreateProductError,
  selectCreateProductStatus,
} from "../features/products/productsSlice";
import { normalizeNumberInput, uniqStrings } from "../shared/format";
import CategoryMultiSelect from "../components/CategoryMultiSelect";
import useGenerateBarcode from "../shared/useGenerateBarcode";

function BarcodePreview({ value, onDelete }) {
  const v = String(value || "").trim();
  if (!v) return null;
  return (
    <div style={styles.barcodeCard} title={v}>
      <div style={styles.barcodeItemRow}>
        <div style={styles.barcodeLabel} title={v}>
          {v}
        </div>
        <div style={styles.barcodeWhite}>
          <Barcode
            value={v}
            height={56}
            width={2}
            marginTop={4}
            marginBottom={4}
            marginLeft={50}
            marginRight={50}
            displayValue
            background="#ffffff"
            lineColor="#000000"
          />
        </div>
        {typeof onDelete === "function" && (
          <button type="button" onClick={() => onDelete(v)} style={styles.barcodeDeleteBtn} title="Удалить штрихкод">
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProductCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const saveStatus = useSelector(selectCreateProductStatus);
  const saveError = useSelector(selectCreateProductError);

  const [form, setForm] = useState({
    name: "",
    barcode: "",
    description: "",
    cost_price: "",
    wholesale_price: "",
    self_cost_price: "",
    is_quick: false,
    is_active: true,
    show_in_cashback: false,
  });

  const [selectedCategories, setSelectedCategories] = useState([]); // [{id,name}]

  const [extraBarcodes, setExtraBarcodes] = useState([]);
  const [newBarcode, setNewBarcode] = useState("");

  const barcodes = useMemo(() => {
    const primary = String(form.barcode || "").trim();
    const extras = uniqStrings(extraBarcodes);
    return uniqStrings([primary, ...extras]);
  }, [extraBarcodes, form.barcode]);

  const { generate, generating, error: generateError } = useGenerateBarcode();

  const canSave = saveStatus !== "saving";

  const handleGenerateBarcode = async () => {
    const generated = await generate();
    if (generated) setForm((f) => ({ ...f, barcode: generated }));
  };

  const addBarcode = () => {
    const s = String(newBarcode || "").trim();
    if (!s) return;
    setExtraBarcodes((prev) => uniqStrings([...(prev || []), s]));
    setNewBarcode("");
  };

  const deleteBarcode = (barcode) => {
    const bc = String(barcode || "").trim();
    if (!bc) return;
    const primary = String(form.barcode || "").trim();
    if (bc === primary) {
      setForm((f) => ({ ...f, barcode: "" }));
      return;
    }
    setExtraBarcodes((prev) => (Array.isArray(prev) ? prev : []).filter((x) => String(x || "").trim() !== bc));
  };

  const onSave = async () => {
    const primaryBarcode = String(form.barcode || "").trim();
    const body = {
      name: String(form.name || "").trim(),
      barcode: primaryBarcode,
      description: String(form.description || ""),
      cost_price: normalizeNumberInput(form.cost_price),
      wholesale_price: normalizeNumberInput(form.wholesale_price),
      self_cost_price: normalizeNumberInput(form.self_cost_price),
      is_quick: Boolean(form.is_quick),
      is_active: Boolean(form.is_active),
      show_in_cashback: Boolean(form.show_in_cashback),
      category_ids: (selectedCategories || []).map((c) => c.id),
    };

    if (body.wholesale_price === null) delete body.wholesale_price;

    if (primaryBarcode) {
      body.barcodes = uniqStrings([primaryBarcode, ...extraBarcodes]);
    }

    const res = await dispatch(createProduct({ body }));
    if (createProduct.fulfilled.match(res)) {
      const createdId = res.payload?.id;
      const from = location.state?.from;
      if (createdId != null) {
        navigate(`/warehouse/products/${createdId}/edit`, { state: { from } });
      } else {
        if (typeof from === "string" && from.startsWith("/")) navigate(from);
        else navigate("/warehouse/products");
      }
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Добавить товар</div>
          <div style={styles.subTitle}>
            Создание товара (данные из <span style={styles.mono}>POST /api/product/products/</span>).
          </div>
        </div>

        <div style={styles.headerActions}>
          <button
            type="button"
            onClick={() => {
              const from = location.state?.from;
              if (typeof from === "string" && from.startsWith("/")) {
                navigate(from);
                return;
              }
              if (window.history.length > 1) {
                navigate(-1);
                return;
              }
              navigate("/warehouse/products");
            }}
            style={styles.btnGhost}
          >
            ← Назад
          </button>
          <button type="button" onClick={onSave} style={styles.btnPrimary} disabled={!canSave}>
            {saveStatus === "saving" ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>

      {saveStatus === "failed" && <div style={styles.error}>{String(saveError || "Failed to save")}</div>}

      <div style={styles.body}>
        <div style={styles.card}>
          <div style={styles.formGrid}>
            <div style={styles.formFieldWide}>
              <div style={styles.label}>Категории (поиск, несколько)</div>
              <CategoryMultiSelect value={selectedCategories} onChange={setSelectedCategories} />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Название</div>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={styles.input}
                placeholder="Название товара"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Штрихкод</div>
              <div style={styles.inlineRow}>
                <input
                  value={form.barcode}
                  onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
                  style={{ ...styles.input, flex: 1, minWidth: 0 }}
                  placeholder="Основной штрихкод"
                />
                <button
                  type="button"
                  onClick={handleGenerateBarcode}
                  style={styles.btn}
                  disabled={generating}
                  title="Сгенерировать внутренний штрихкод"
                >
                  {generating ? "…" : "Сгенерировать"}
                </button>
              </div>
              {generateError ? <div style={styles.errorHint}>{generateError}</div> : null}
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Стоимость</div>
              <input
                value={String(form.cost_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, cost_price: e.target.value }))}
                style={styles.input}
                placeholder="например 100.00"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Оптовая цена (wholesale_price)</div>
              <input
                value={String(form.wholesale_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, wholesale_price: e.target.value }))}
                style={styles.input}
                placeholder="например 120.00"
              />
            </div>

            <div style={styles.formField}>
              <div style={styles.label}>Себестоимость (self_cost_price)</div>
              <input
                value={String(form.self_cost_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, self_cost_price: e.target.value }))}
                style={styles.input}
                placeholder="например 95.00"
              />
            </div>

            <div style={styles.formFieldWide}>
              <div style={styles.label}>Описание</div>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                style={styles.textarea}
                placeholder="Описание"
                rows={4}
              />
            </div>

            <div style={styles.formField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form.is_quick)}
                  onChange={(e) => setForm((f) => ({ ...f, is_quick: e.target.checked }))}
                />{" "}
                <span>Быстрый товар (is_quick)</span>
              </label>
            </div>

            <div style={styles.formField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form.is_active)}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />{" "}
                <span>Активный (is_active)</span>
              </label>
            </div>

            <div style={styles.formField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(form.show_in_cashback)}
                  onChange={(e) => setForm((f) => ({ ...f, show_in_cashback: e.target.checked }))}
                />{" "}
                <span>Показывать в auf cashback</span>
              </label>
            </div>

            <div style={styles.formFieldWide}>
              <div style={styles.label}>Доп. штрихкоды</div>
              <div style={styles.inlineRow}>
                <input
                  value={newBarcode}
                  onChange={(e) => setNewBarcode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addBarcode();
                    }
                  }}
                  style={styles.input}
                  placeholder="Добавить доп. штрихкод…"
                />
                <button type="button" onClick={addBarcode} style={styles.btn} disabled={!String(newBarcode || "").trim()}>
                  + Добавить
                </button>
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={styles.label}>Предпросмотр штрихкодов</div>
                {barcodes.length === 0 ? (
                  <div style={styles.hint}>Штрихкоды отсутствуют</div>
                ) : (
                  <div style={styles.barcodeRow}>
                    {barcodes.map((bc) => (
                      <BarcodePreview key={bc} value={bc} onDelete={deleteBarcode} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-bg)",
    color: "var(--c-text)",
  },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  title: { fontSize: 20, fontWeight: 900 },
  subTitle: { marginTop: 6, opacity: 0.75, fontSize: 14, lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  btn: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnGhost: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    borderTop: "1px solid var(--c-danger)",
    borderBottom: "1px solid var(--c-danger)",
  },
  body: { padding: 18, minHeight: 0, overflow: "auto", display: "grid", gap: 12 },
  card: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 14,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  formField: { display: "grid", gap: 6 },
  formFieldWide: { display: "grid", gap: 6, gridColumn: "1 / -1" },
  inlineRow: { display: "flex", gap: 10, alignItems: "center" },
  label: { fontSize: 13, color: "var(--c-text-dim)", fontWeight: 900 },
  input: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  textarea: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    resize: "vertical",
  },
  checkboxLabel: { fontSize: 15, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" },
  hint: { fontSize: 15, opacity: 0.75, fontWeight: 800 },
  errorHint: { fontSize: 14, fontWeight: 800, color: "var(--c-danger)" },
  barcodeRow: { display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" },
  barcodeCard: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 10,
    width: "100%",
  },
  barcodeItemRow: { display: "flex", alignItems: "center", gap: 12, width: "100%" },
  barcodeLabel: {
    width: 220,
    maxWidth: "40vw",
    fontSize: 14,
    fontWeight: 900,
    opacity: 0.9,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barcodeWhite: {
    flex: 1,
    background: "#ffffff",
    borderRadius: 10,
    padding: "10px 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  barcodeDeleteBtn: {
    width: 34,
    height: 34,
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    lineHeight: 1,
  },
};

